import {
  ActionIcon,
  type ActionIconProps,
  Box,
  Indicator,
  IndicatorProps,
  Menu,
  type MenuProps,
  ScrollArea,
} from '@mantine/core';
import type { IconProps, Icon as TablerIcon } from '@tabler/icons-react';
import IconDotsVertical from '@tabler/icons-react/dist/esm/icons/IconDotsVertical.mjs';
import {
  createContext,
  isValidElement,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

export const MenuContext = createContext<{
  setOpened: (val: boolean) => void;
}>(null);

interface Props extends MenuProps {
  icon?: TablerIcon | ReactNode; // Accept both component type and ReactNode
  actionIconProps?: ActionIconProps & {
    'aria-label'?: string;
    onMouseEnter?: () => void;
    'data-testid'?: string;
  };
  iconProps?: IconProps;
  target?: ReactElement;
  onMouseEnter?: () => void;
  indicator?: boolean;
  indicatorProps?: IndicatorProps;
}

export const ActionsMenu = ({
  children,
  icon,
  actionIconProps,
  iconProps,
  onMouseEnter,
  disabled,
  indicator,
  indicatorProps,
  ...rest
}: Props) => {
  const { t } = useTranslation(['dashboard', 'translation', 'subscriptions']);
  const [opened, setOpened] = useState(false);

  // Render icon based on its type
  const renderIcon = () => {
    if (!icon) {
      return <IconDotsVertical size={16} {...iconProps} />;
    }

    // If it's already a rendered element, use it directly
    if (isValidElement(icon)) {
      return icon;
    }

    // If it's a component type, render it
    const IconComponent = icon as TablerIcon;
    return <IconComponent size={16} {...iconProps} />;
  };

  const trigger = (
    <ActionIcon
      size='sm'
      variant={'subtle'}
      disabled={disabled}
      data-testid='actions-menu'
      {...actionIconProps}
    >
      {renderIcon()}
    </ActionIcon>
  );

  return (
    <Menu
      withinPortal={false}
      withArrow
      shadow={'xl'}
      arrowPosition={'center'}
      position={'bottom-end'}
      trigger={'click-hover'}
      opened={opened}
      onChange={setOpened}
      offset={0}
      onClose={() => setOpened(false)}
      disabled={disabled}
      {...rest}
    >
      <Menu.Target>
        {indicator ? (
          <Indicator inline size={8} {...indicatorProps}>
            {trigger}
          </Indicator>
        ) : (
          trigger
        )}
      </Menu.Target>
      <Menu.Dropdown>
        <Box onMouseEnter={onMouseEnter}>
          <Menu.Label ta={'left'}>{t('translation:action.action')}</Menu.Label>
          <ScrollArea.Autosize
            mah='60dvh'
            offsetScrollbars='present'
            scrollbars='y'
          >
            <MenuContext.Provider value={{ setOpened }}>
              {children}
            </MenuContext.Provider>
            <Menu.Label className='hidden only:!block'>
              {t('text.noAvailableActions', { ns: 'translation' })}
            </Menu.Label>
          </ScrollArea.Autosize>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};